// Daily-cost area chart for the Home overview. Same data source and same
// "backfill missing days as zero" rule as the Usage page's CostChart, just
// styled for a wider tile and a quick-glance read (no axis ticks below the
// strict minimum). The full chart with details lives at /usage; this is
// the first-screen pulse.

import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Activity, ArrowRight } from 'lucide-react';
import type { UsageSummary } from '@cortex/shared';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card.js';
import { EmptyState } from '../ui/empty-state.js';
import { Button } from '../ui/button.js';
import { backfillSeries, shortDay } from '../../lib/usage-helpers.js';
import { formatUsd } from '../../lib/format-money.js';
import { useTheme } from '../../lib/theme.js';

interface CostTrendCardProps {
  summary: UsageSummary;
}

export function CostTrendCard({ summary }: CostTrendCardProps) {
  const { resolvedTheme } = useTheme();
  const data = useMemo(
    () =>
      backfillSeries(summary.series, summary.plan.currentPeriodStart, summary.plan.currentPeriodEnd),
    [summary.series, summary.plan.currentPeriodStart, summary.plan.currentPeriodEnd],
  );

  // Theme-aware grid + tick colors. Recharts doesn't read CSS vars from a
  // mounted style sheet, so we resolve the rgba values ourselves the same
  // way the Usage page chart does.
  const isDark = resolvedTheme === 'dark';
  const gridColor = isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)';
  const textColor = isDark ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.6)';

  return (
    <Card className="h-full">
      <CardHeader className="flex flex-row items-start justify-between gap-3 space-y-0">
        <div className="space-y-1.5">
          <CardTitle>Daily cost</CardTitle>
          {/* Surface the period total here so users can see the chart breaks
              down the same number the Cost KPI shows ("Daily cost" is the
              KPI distributed across days; without this line, two figures
              for the "same" metric look like a discrepancy). */}
          <CardDescription>
            <span className="font-medium text-foreground">
              {formatUsd(summary.period.costUsd)}
            </span>{' '}
            total · USD per day this billing period.
          </CardDescription>
        </div>
        <Button variant="ghost" size="sm" asChild>
          <Link to="/usage" aria-label="View usage details">
            View details
            <ArrowRight className="h-4 w-4" aria-hidden />
          </Link>
        </Button>
      </CardHeader>
      <CardContent>
        {data.length === 0 ? (
          <EmptyState
            icon={Activity}
            title="No usage yet"
            description="Once your assistant handles its first conversation, the trend will appear here."
            className="border-0 bg-transparent py-8"
          />
        ) : (
          // Numeric height (matches Tailwind h-56 = 14rem = 224px) on the
          // ResponsiveContainer instead of the percentage pattern. With
          // `height="100%"` Recharts measures the parent on first paint
          // and emits "width(-1) and height(-1)" warnings under React
          // StrictMode (dev-only) before the ResizeObserver fires. An
          // explicit numeric height removes the height side of the
          // measurement entirely; width="100%" keeps the chart responsive
          // horizontally.
          <div data-testid="home-cost-trend" className="w-full">
            <ResponsiveContainer width="100%" height={192}>
              <AreaChart data={data} margin={{ top: 8, right: 8, bottom: 8, left: 0 }}>
                <defs>
                  <linearGradient id="home-cost-gradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity={0.35} />
                    <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke={gridColor} vertical={false} />
                <XAxis
                  dataKey="bucket"
                  fontSize={11}
                  tickLine={false}
                  axisLine={false}
                  tickMargin={8}
                  stroke={textColor}
                  tickFormatter={(v: string) => shortDay(v)}
                  minTickGap={24}
                />
                <YAxis
                  fontSize={11}
                  tickLine={false}
                  axisLine={false}
                  tickMargin={8}
                  stroke={textColor}
                  width={48}
                  tickFormatter={(v: number) => `$${v.toFixed(2)}`}
                />
                <RechartsTooltip
                  contentStyle={{
                    background: 'hsl(var(--popover))',
                    border: '1px solid hsl(var(--border))',
                    borderRadius: 6,
                    fontSize: 12,
                    color: 'hsl(var(--popover-foreground))',
                    boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
                  }}
                  labelStyle={{ color: 'hsl(var(--muted-foreground))' }}
                  labelFormatter={(label) =>
                    typeof label === 'string' ? shortDay(label) : String(label)
                  }
                  formatter={(value) => [
                    `$${typeof value === 'number' ? value.toFixed(4) : String(value)}`,
                    'Cost',
                  ]}
                />
                <Area
                  type="monotone"
                  dataKey="costUsd"
                  stroke="hsl(var(--primary))"
                  strokeWidth={2}
                  fill="url(#home-cost-gradient)"
                  activeDot={{ r: 4, strokeWidth: 0, fill: 'hsl(var(--primary))' }}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
